import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Alert,
  Backdrop,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TablePagination,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { Person, Home, Phone, Email, Badge, AttachMoney, TrendingUp, DateRange, Schedule, ArrowBack, Search as SearchIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthPage from './components/AuthPage';
import Header from './components/Header';
import SearchForm from './components/SearchForm';
import TabbedResults from './components/TabbedResults';
import BackToTop from './components/BackToTop';
import BatchPage from './components/BatchPage';
import ViewPage from './components/ViewPage';
import ViewsListPage from './components/ViewsListPage';
import EnrichmentPage from './components/EnrichmentPage';
import EnrichmentResultsPage from './components/EnrichmentResultsPage';
import ProfileSettingsModal from './components/ProfileSettingsModal';
import { searchKnowledgeCore, getRecentSearches, clearRecentSearches, deleteSelectedSearches } from './services/api';
import { buildAIProfileData } from './services/aiProfileData';
import { SearchFormData, SearchResult, ViewEnrichmentRowStatus } from './types';

// Create enhanced modern theme with gradient background
const buildTheme = (mode: 'light' | 'dark') => createTheme({
  palette: {
    mode,
    primary: { main: '#2E3B55', light: '#4A5D7A', dark: '#1C2433' },
    secondary: { main: '#00C853', light: '#5EFC82', dark: '#009624' },
    background: {
      default: mode === 'light' ? '#E5E7EB' : '#0F172A',
      paper:   mode === 'light' ? 'rgba(255,255,255,0.95)' : 'rgba(30,41,59,0.97)',
    },
    text: {
      primary:   mode === 'light' ? '#2E3B55' : '#E2E8F0',
      secondary: mode === 'light' ? '#5A6C7D' : '#94A3B8',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: mode === 'light' ? '#E5E7EB' : '#0F172A',
          minHeight: '100vh',
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: mode === 'light' ? 'rgba(255,255,255,0.95)' : 'rgba(30,41,59,0.97)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${mode === 'light' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.05)'}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiContainer: { styleOverrides: { root: { background: 'transparent' } } },
  },
});


// Main app content component (protected)
const AppContent: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResult | null>(null);
  const [searchCriteria, setSearchCriteria] = useState<SearchFormData | null>(null);
  const aiTestProfileData = useMemo(
    () => results ? buildAIProfileData(results, searchCriteria) : undefined,
    [results, searchCriteria],
  );
  const [recentSearches, setRecentSearches] = useState<any[]>([]);
  const [showRecentSearchesPage, setShowRecentSearchesPage] = useState(false);
  const [paginationPage, setPaginationPage] = useState(0);
  const [paginationRowsPerPage, setPaginationRowsPerPage] = useState(10);
  const [selectedSearchIds, setSelectedSearchIds] = useState<Set<number>>(new Set());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showBatchPage, setShowBatchPage] = useState(false);
  const [showViewPage, setShowViewPage] = useState(false);
  const [showViewsListPage, setShowViewsListPage] = useState(false);
  const [showEnrichPage, setShowEnrichPage] = useState(false);
  const [showEnrichResultsPage, setShowEnrichResultsPage] = useState(false);
  const [activeViewId, setActiveViewId] = useState<number | null>(null);
  const [activeViewName, setActiveViewName] = useState<string>('');
  const [enrichJobId, setEnrichJobId] = useState<number | null>(null);
  const [enrichRows, setEnrichRows] = useState<ViewEnrichmentRowStatus[]>([]);

  // Navigation history stack — each entry is the page to return to on back()
  type PageName = 'home' | 'batch' | 'view' | 'viewsList' | 'recent' | 'enrich' | 'enrichResults';
  const [, setPageStack] = useState<PageName[]>([]);

  // Ref always holds the current page — safe to read from event listeners
  const currentPageRef = useRef<PageName>('home');
  currentPageRef.current =
    showEnrichResultsPage ? 'enrichResults'
    : showEnrichPage      ? 'enrich'
    : showViewPage        ? 'view'
    : showViewsListPage   ? 'viewsList'
    : showBatchPage       ? 'batch'
    : showRecentSearchesPage ? 'recent'
    : 'home';

  const navigateTo = (page: PageName, viewId?: number) => {
    setPageStack(prev => [...prev, currentPageRef.current]);
    setShowBatchPage(page === 'batch');
    setShowViewPage(page === 'view');
    setShowViewsListPage(page === 'viewsList');
    setShowEnrichPage(page === 'enrich');
    setShowEnrichResultsPage(page === 'enrichResults');
    setShowRecentSearchesPage(page === 'recent');
    if (page === 'view' && viewId !== undefined) setActiveViewId(viewId);
  };

  const navigateBack = () => {
    setPageStack(prev => {
      const stack = [...prev];
      const dest = stack.pop() ?? 'home';
      setShowBatchPage(dest === 'batch');
      setShowViewPage(dest === 'view');
      setShowViewsListPage(dest === 'viewsList');
      setShowEnrichPage(dest === 'enrich');
      setShowEnrichResultsPage(dest === 'enrichResults');
      setShowRecentSearchesPage(dest === 'recent');
      return stack;
    });
  };
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>(
    (localStorage.getItem('kc:pref:theme') as 'light' | 'dark') || 'light'
  );
  const { isAuthenticated, isLoading } = useAuth();

  // On login, auto-navigate to batch page if a job was running
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const saved = localStorage.getItem('kc:batch:active_job');
      if (saved) {
        try {
          const { startedAt } = JSON.parse(saved);
          const ageMs = Date.now() - new Date(startedAt).getTime();
          if (ageMs <= 24 * 60 * 60 * 1000) navigateTo('batch');
          else localStorage.removeItem('kc:batch:active_job');
        } catch { localStorage.removeItem('kc:batch:active_job'); }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  // Load recent searches on mount and after successful search
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      loadRecentSearches();
    }
  }, [isAuthenticated, isLoading]);

  // Listen for header dropdown "Recent Searches" click
  useEffect(() => {
    const handler = () => {
      navigateTo('recent');
    };
    window.addEventListener('kc:openRecentSearches', handler);
    return () => window.removeEventListener('kc:openRecentSearches', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for Profile Settings click from header
  useEffect(() => {
    const handler = () => setShowProfileModal(true);
    window.addEventListener('kc:openProfileSettings', handler);
    return () => window.removeEventListener('kc:openProfileSettings', handler);
  }, []);

  // Listen for theme change from modal
  useEffect(() => {
    const handler = (e: Event) => {
      const mode = (e as CustomEvent).detail as 'light' | 'dark';
      setCurrentTheme(mode);
    };
    window.addEventListener('kc:setTheme', handler);
    return () => window.removeEventListener('kc:setTheme', handler);
  }, []);

  const loadRecentSearches = async () => {
    try {
      const response = await getRecentSearches();
      if (response && response.searches) {
        setRecentSearches(response.searches);
      }
    } catch (err) {
      console.warn('Failed to load recent searches:', err instanceof Error ? err.message : 'Unknown error');
      // Don't fail the app if recent searches fail to load
    }
  };

  const clearSearchHistory = async () => {
    if (window.confirm('Are you sure you want to clear all search history? This cannot be undone.')) {
      try {
        await clearRecentSearches();
        setRecentSearches([]);
        setSelectedSearchIds(new Set());
      } catch (err) {
        console.error('Failed to clear search history:', err);
      }
    }
  };

  const handleToggleSelectSearch = (searchId: number) => {
    const newSelected = new Set(selectedSearchIds);
    if (newSelected.has(searchId)) {
      newSelected.delete(searchId);
    } else {
      newSelected.add(searchId);
    }
    setSelectedSearchIds(newSelected);
  };

  const handleSelectAllSearches = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all searches on current page
      const currentPageSearches = recentSearches.slice(
        paginationPage * paginationRowsPerPage,
        paginationPage * paginationRowsPerPage + paginationRowsPerPage
      );
      setSelectedSearchIds(new Set(currentPageSearches.map(search => search.id)));
    } else {
      setSelectedSearchIds(new Set());
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedSearchIds.size === 0) {
      alert('Please select at least one search to delete.');
      return;
    }
    
    if (window.confirm(`Delete ${selectedSearchIds.size} selected search(es)? This cannot be undone.`)) {
      try {
        const searchIds = Array.from(selectedSearchIds);
        await deleteSelectedSearches(searchIds);
        setRecentSearches(recentSearches.filter(search => !selectedSearchIds.has(search.id)));
        setSelectedSearchIds(new Set());
      } catch (err) {
        console.error('Failed to delete selected searches:', err);
      }
    }
  };

  const handleSearch = async (formData: SearchFormData) => {
    setLoading(true);
    setError(null);
    setResults(null);
    setSearchCriteria(formData); // Store the search criteria

    try {
      // Single API call - backend returns all data (database + Experian + DataIris + phone + email validation)
      const data = await searchKnowledgeCore(formData);
      
      console.log('Unified search response received:', data);
      
      // Data already contains all information from all sources
      // No need for separate DataIris, phone validation calls
      setResults(data);
      
      // Auto-collapse sidebar to give more space to results
      setSidebarCollapsed(true);
      
      // Reload recent searches after successful search
      loadRecentSearches();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setSearchCriteria(null); // Clear criteria on error
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <video 
          autoPlay 
          loop 
          muted 
          style={{ 
            width: '40px', 
            height: '40px'
          }}
        >
          <source src="/rolling_com.mp4" type="video/mp4" />
        </video>
      </Box>
    );
  }

  // Show auth page if not authenticated
  if (!isAuthenticated) {
    return <AuthPage />;
  }

  // Show views list page
  if (showViewsListPage) {
    return (
      <>
        <Header onBatchClick={() => navigateTo('batch')} onViewsClick={() => navigateTo('viewsList')} aiTestProfileData={aiTestProfileData} />
        <Box sx={{ pt: '64px' }}>
          <ViewsListPage
            onBack={() => {
              setShowViewsListPage(false);
              setShowBatchPage(false);
              setShowViewPage(false);
              setShowRecentSearchesPage(false);
              setPageStack([]);
            }}
            onOpenView={(viewId) => navigateTo('view', viewId)}
          />
        </Box>
        <ProfileSettingsModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          currentTheme={currentTheme}
          onThemeChange={(mode) => window.dispatchEvent(new CustomEvent('kc:setTheme', { detail: mode }))}
        />
      </>
    );
  }

  // Show view page
  if (showViewPage && activeViewId !== null) {
    return (
      <>
        <Header onBatchClick={() => navigateTo('batch')} onViewsClick={() => navigateTo('viewsList')} aiTestProfileData={aiTestProfileData} />
        <Box sx={{ pt: '64px' }}>
          <ViewPage
            viewId={activeViewId}
            onBack={navigateBack}
            onRunFullSearch={(jobId, vName) => {
              setEnrichJobId(jobId);
              setActiveViewName(vName);
              navigateTo('enrich');
            }}
          />
        </Box>
        <ProfileSettingsModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          currentTheme={currentTheme}
          onThemeChange={(mode) => window.dispatchEvent(new CustomEvent('kc:setTheme', { detail: mode }))}
        />
      </>
    );
  }

  // Show enrichment progress page
  if (showEnrichPage && activeViewId !== null && enrichJobId !== null) {
    return (
      <>
        <Header onBatchClick={() => navigateTo('batch')} onViewsClick={() => navigateTo('viewsList')} aiTestProfileData={aiTestProfileData} />
        <Box sx={{ pt: '64px' }}>
          <EnrichmentPage
            viewId={activeViewId}
            viewName={activeViewName}
            jobId={enrichJobId}
            onBack={navigateBack}
            onComplete={(rows) => {
              setEnrichRows(rows);
              navigateTo('enrichResults');
            }}
          />
        </Box>
        <ProfileSettingsModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          currentTheme={currentTheme}
          onThemeChange={(mode) => window.dispatchEvent(new CustomEvent('kc:setTheme', { detail: mode }))}
        />
      </>
    );
  }

  // Show enrichment results page
  if (showEnrichResultsPage && enrichRows.length > 0) {
    return (
      <>
        <Header onBatchClick={() => navigateTo('batch')} onViewsClick={() => navigateTo('viewsList')} aiTestProfileData={aiTestProfileData} />
        <Box sx={{ pt: '64px' }}>
          <EnrichmentResultsPage
            viewName={activeViewName}
            rows={enrichRows}
            onBack={navigateBack}
          />
        </Box>
        <ProfileSettingsModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          currentTheme={currentTheme}
          onThemeChange={(mode) => window.dispatchEvent(new CustomEvent('kc:setTheme', { detail: mode }))}
        />
      </>
    );
  }

  // Show batch page
  if (showBatchPage) {
    return (
      <>
        <Header onBatchClick={() => navigateTo('batch')} onViewsClick={() => navigateTo('viewsList')} aiTestProfileData={aiTestProfileData} />
        <Box sx={{ pt: '64px' }}>
          <BatchPage
            onBack={navigateBack}
            onViewCreated={(viewId) => navigateTo('view', viewId)}
            onMyViews={() => navigateTo('viewsList')}
          />
        </Box>
        <ProfileSettingsModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          currentTheme={currentTheme}
          onThemeChange={(mode) => window.dispatchEvent(new CustomEvent('kc:setTheme', { detail: mode }))}
        />
      </>
    );
  }

  return (
    <>
      <Header onBatchClick={() => navigateTo('batch')} onViewsClick={() => navigateTo('viewsList')} aiTestProfileData={aiTestProfileData} />
      <Container maxWidth={false} sx={{ pt: 12, pb: 4, px: 3, maxWidth: '100vw', position: 'relative' }}>
      
      {showRecentSearchesPage ? (
        // Recent Searches Full Page - CRM Table Format
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#f5f5f5' }}>
          <Box sx={{ p: 3, backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton 
                  onClick={navigateBack}
                  sx={{ color: '#2E3B55' }}
                >
                  <ArrowBack />
                </IconButton>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#2E3B55' }}>
                    Recent Searches
                  </Typography>
                  {selectedSearchIds.size > 0 && (
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                      {selectedSearchIds.size} selected
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {selectedSearchIds.size > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleDeleteSelected}
                  >
                    Delete Selected ({selectedSearchIds.size})
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={clearSearchHistory}
                >
                  Clear All
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <TableContainer component={Paper} sx={{ backgroundColor: 'white' }}>
              {recentSearches && recentSearches.length > 0 ? (
                <>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #2E3B55' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#2E3B55', width: '8%', borderRight: '1px solid #e0e0e0' }}>No.</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#2E3B55', width: '14%', borderRight: '1px solid #e0e0e0' }}>First Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#2E3B55', width: '14%', borderRight: '1px solid #e0e0e0' }}>Last Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#2E3B55', width: '20%', borderRight: '1px solid #e0e0e0' }}>Address</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#2E3B55', width: '15%', borderRight: '1px solid #e0e0e0' }}>City, State, ZIP</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#2E3B55', width: '14%', borderRight: '1px solid #e0e0e0' }}>Searched</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#2E3B55', width: '8%', borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#2E3B55', width: '3%', padding: '8px', textAlign: 'center' }}>
                          <Checkbox
                            checked={selectedSearchIds.size > 0 && selectedSearchIds.size === recentSearches.slice(paginationPage * paginationRowsPerPage, paginationPage * paginationRowsPerPage + paginationRowsPerPage).length}
                            indeterminate={selectedSearchIds.size > 0 && selectedSearchIds.size < recentSearches.slice(paginationPage * paginationRowsPerPage, paginationPage * paginationRowsPerPage + paginationRowsPerPage).length}
                            onChange={handleSelectAllSearches}
                          />
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentSearches
                        .slice(paginationPage * paginationRowsPerPage, paginationPage * paginationRowsPerPage + paginationRowsPerPage)
                        .map((search, index) => (
                          <TableRow 
                            key={search.id}
                            sx={{ 
                              '&:hover': { 
                                backgroundColor: 'rgba(46, 59, 85, 0.05)',
                                transition: 'background-color 0.2s ease'
                              },
                              borderBottom: '1px solid #e0e0e0',
                              backgroundColor: selectedSearchIds.has(search.id) ? 'rgba(46, 59, 85, 0.1)' : 'inherit'
                            }}
                          >
                            <TableCell sx={{ color: '#2E3B55', fontWeight: 500, borderRight: '1px solid #e0e0e0' }}>
                              {paginationPage * paginationRowsPerPage + index + 1}
                            </TableCell>
                            <TableCell sx={{ color: '#2E3B55', borderRight: '1px solid #e0e0e0' }}>{search.first_name || '-'}</TableCell>
                            <TableCell sx={{ color: '#2E3B55', borderRight: '1px solid #e0e0e0' }}>{search.last_name || '-'}</TableCell>
                            <TableCell sx={{ color: '#5A6C7D', borderRight: '1px solid #e0e0e0' }}>{search.street || '-'}</TableCell>
                            <TableCell sx={{ color: '#5A6C7D', borderRight: '1px solid #e0e0e0' }}>
                              {[search.city, search.state, search.zip_code].filter(Boolean).join(', ') || '-'}
                            </TableCell>
                            <TableCell sx={{ color: '#999', fontSize: '0.9rem', borderRight: '1px solid #e0e0e0' }}>{search.date}</TableCell>
                            <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #e0e0e0' }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const formData: SearchFormData = {
                                    FIRST_NAME: search.first_name || '',
                                    LAST_NAME: search.last_name || '',
                                    STREET1: search.street || '',
                                    STREET2: '',
                                    CITY: search.city || '',
                                    STATE: search.state || '',
                                    ZIP: search.zip_code || '',
                                  };
                                  navigateBack();
                                  handleSearch(formData);
                                }}
                                sx={{ color: '#2E3B55', '&:hover': { backgroundColor: 'rgba(46, 59, 85, 0.1)' } }}
                              >
                                <SearchIcon />
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ padding: '8px', textAlign: 'center' }}>
                              <Checkbox
                                checked={selectedSearchIds.has(search.id)}
                                onChange={() => handleToggleSelectSearch(search.id)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={recentSearches.length}
                    rowsPerPage={paginationRowsPerPage}
                    page={paginationPage}
                    onPageChange={(event, newPage) => setPaginationPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setPaginationRowsPerPage(parseInt(event.target.value, 10));
                      setPaginationPage(0);
                    }}
                    sx={{
                      backgroundColor: '#f9f9f9',
                      borderTop: '1px solid #e0e0e0',
                      '& .MuiTablePagination-select': {
                        marginRight: '8px',
                      }
                    }}
                  />
                </>
              ) : (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    No recent searches yet
                  </Typography>
                </Box>
              )}
            </TableContainer>
          </Box>
        </Box>
      ) : (
        // Results layout (grid with left search, right results) - ALWAYS SHOWN
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: sidebarCollapsed ? '35px 1fr' : '400px 1fr',
          gap: 3,
          minHeight: 'calc(100vh - 200px)',
          transition: 'grid-template-columns 0.3s ease',
        }}>
          {/* Search Panel */}
          <Box sx={{ 
            position: 'sticky',
            top: 80, // Account for sticky header height (64px + margin)
            height: 'fit-content'
          }}>
            <Paper elevation={3} sx={{ 
              p: sidebarCollapsed ? '4px' : 3, 
              height: 'fit-content', 
              transition: 'padding 0.3s ease', 
              position: 'relative', 
              display: sidebarCollapsed ? 'flex' : 'block',
              alignItems: sidebarCollapsed ? 'center' : 'unset',
              justifyContent: sidebarCollapsed ? 'center' : 'unset',
              minHeight: sidebarCollapsed ? '35px' : 'auto' 
            }}>
              {/* Collapse/Expand Button - Positioned at top right */}
              <Box sx={{ 
                position: sidebarCollapsed ? 'relative' : 'absolute',
                top: sidebarCollapsed ? 0 : 12,
                right: sidebarCollapsed ? 0 : 12,
                zIndex: 10
              }}>
                <Tooltip title={sidebarCollapsed ? 'Expand sidebar for search form' : 'Collapse sidebar for more space'} placement="left" arrow>
                  <IconButton
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    size="small"
                    sx={{ 
                      color: '#ffffff',
                      backgroundColor: '#2E3B55',
                      '&:hover': {
                        backgroundColor: '#1C2433',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s ease',
                      padding: '4px'
                    }}
                  >
                    {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Sidebar Content - Hidden when collapsed */}
              {!sidebarCollapsed && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ pr: 5 }}>
                    Search Information
                  </Typography>
                  <SearchForm onSubmit={handleSearch} />
                  
                  {/* Recent Searches Section */}
                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                      Recent Searches
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {recentSearches && recentSearches.length > 0 ? (
                        recentSearches.slice(0, 5).map((search) => (
                          <Box 
                            key={search.id}
                            sx={{ 
                              p: 1.5, 
                              borderRadius: 1.5, 
                              backgroundColor: 'rgba(52, 152, 219, 0.04)',
                              border: '1px solid rgba(52, 152, 219, 0.08)',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                borderColor: 'rgba(52, 152, 219, 0.15)',
                                transform: 'translateY(-1px)'
                              }
                            }}
                            onClick={() => {
                              const formData: SearchFormData = {
                                FIRST_NAME: search.first_name || '',
                                LAST_NAME: search.last_name || '',
                                STREET1: search.street || '',
                                STREET2: '',
                                CITY: search.city || '',
                                STATE: search.state || '',
                                ZIP: search.zip_code || '',
                              };
                              handleSearch(formData);
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C3E50', fontSize: '0.85rem', mb: 0.3 }}>
                              {search.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#566573', fontSize: '0.75rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.3 }}>
                              {search.address}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#7B8794', fontSize: '0.7rem' }}>
                              {search.date}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                          No recent searches
                        </Typography>
                      )}
                    </Box>
                    {recentSearches && recentSearches.length > 0 && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#3498db', 
                          cursor: 'pointer', 
                          mt: 1.5, 
                          display: 'block',
                          fontWeight: 600,
                          textAlign: 'center',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => navigateTo('recent')}
                      >
                        View all ({recentSearches.length})
                      </Typography>
                    )}
                  </Box>
                  
                  {error && (
                    <Alert severity="error" sx={{ mt: 3 }}>
                      {error}
                    </Alert>
                  )}
                </>
              )}
            </Paper>
          </Box>

          {/* Results Panel */}
          <Box sx={{ minWidth: 0, overflow: 'visible', height: 'fit-content', position: 'relative' }}>
            {/* Fade mask to hide content behind sticky header */}
            {searchCriteria && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 64, // Start right after main header
                  left: sidebarCollapsed ? 98 : 440, // Adjust based on sidebar state (50px + gap when collapsed, 400px + gap when expanded)
                  right: 24, // Match container padding
                  height: 140, // Cover area where sticky header will be
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 30%, transparent 60%)',
                  backdropFilter: 'blur(10px)',
                  zIndex: 1150,
                  pointerEvents: 'none',
                  transition: 'left 0.3s ease',
                }}
              />
            )}
            
            {/* Search Summary Header - Sticky within results column */}
            {searchCriteria && (
              <Box
                sx={{ 
                  p: 2.5, 
                  mb: 3, 
                  backgroundColor: '#283E56',
                  color: 'white',
                  borderRadius: 3,
                  position: 'sticky',
                  top: 80, // Account for sticky header height (64px + margin)
                  zIndex: 1200,
                  overflow: 'visible',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-3px',
                    left: '-3px',
                    right: '-3px',
                    bottom: '-3px',
                    background: 'linear-gradient(0deg, #3498db, #2ecc71, #f39c12, #e74c3c, #9b59b6, #3498db)',
                    borderRadius: '15px',
                    zIndex: -1,
                    animation: 'rotatingBorder 4s linear infinite',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '0px',
                    left: '0px',
                    right: '0px',
                    bottom: '0px',
                    background: '#283E56',
                    borderRadius: '12px',
                    zIndex: -1,
                  },
                  animation: 'slideInDown 0.6s ease-out',
                  '@keyframes slideInDown': {
                    '0%': {
                      transform: 'translateY(-30px)',
                      opacity: 0,
                    },
                    '100%': {
                      transform: 'translateY(0)',
                      opacity: 1,
                    },
                  },
                  '@keyframes rotatingBorder': {
                    '0%': {
                      background: 'linear-gradient(0deg, #3498db, #2ecc71, #f39c12, #e74c3c, #9b59b6, #3498db)',
                    },
                    '25%': {
                      background: 'linear-gradient(90deg, #3498db, #2ecc71, #f39c12, #e74c3c, #9b59b6, #3498db)',
                    },
                    '50%': {
                      background: 'linear-gradient(180deg, #3498db, #2ecc71, #f39c12, #e74c3c, #9b59b6, #3498db)',
                    },
                    '75%': {
                      background: 'linear-gradient(270deg, #3498db, #2ecc71, #f39c12, #e74c3c, #9b59b6, #3498db)',
                    },
                    '100%': {
                      background: 'linear-gradient(360deg, #3498db, #2ecc71, #f39c12, #e74c3c, #9b59b6, #3498db)',
                    },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(40, 62, 86, 0.3)',
                    transition: 'all 0.3s ease',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Name Row */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                    {/* Name */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person sx={{ fontSize: 20, opacity: 0.8, flexShrink: 0 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.9, fontSize: '0.9rem' }}>
                            Name
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 600, letterSpacing: '0.3px', fontSize: '1.8rem' }}>
                            {searchCriteria.FIRST_NAME} {searchCriteria.LAST_NAME}
                          </Typography>
                        </Box>
                      </Box>
                  </Box>

                  {/* Address, Phone, Email, Constituent ID Row */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                    {/* Address */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Home sx={{ fontSize: 18, opacity: 0.8, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.9 }}>
                          Address
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4, opacity: 0.95, fontSize: '1.4rem' }}>
                          {searchCriteria.STREET1}
                          {searchCriteria.STREET2 && `, ${searchCriteria.STREET2}`}
                          <br />
                          {searchCriteria.CITY}, {searchCriteria.STATE} {searchCriteria.ZIP}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Phone */}
                    {results?.source === 'database' && results?.results?.consumer_behavior?.records?.[0]?.contact_info ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone sx={{ fontSize: 18, opacity: 0.8, flexShrink: 0 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.9 }}>
                            Phone
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, opacity: 0.95, fontSize: '1.4rem' }}>
                            {results.results.consumer_behavior.records[0].contact_info.phone || 'Not Available'}
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}

                    {/* Email */}
                    {results?.source === 'database' && results?.results?.consumer_behavior?.records?.[0]?.contact_info ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email sx={{ fontSize: 18, opacity: 0.8, flexShrink: 0 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.9 }}>
                            Email
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, opacity: 0.95, lineHeight: 1.4, wordBreak: 'break-word', fontSize: '1.4rem' }}>
                            {results.results.consumer_behavior.records[0].contact_info.email || 'Not Available'}
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}

                    {/* Constituent ID */}
                    {results?.source === 'database' && results?.results?.consumer_behavior?.records?.[0]?.contact_info ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Badge sx={{ fontSize: 18, opacity: 0.8, flexShrink: 0 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.9 }}>
                            Constituent ID
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, opacity: 0.95, fontSize: '1.4rem' }}>
                            {results.results.consumer_behavior.records[0].contact_info.constituent_id || 'Not Available'}
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}
                  </Box>

                  {/* Dashboard Tiles */}
                  {results?.source === 'database' && results?.results?.consumer_behavior?.records?.[0]?.contact_info ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2 }}>
                      {/* Largest Gift Tile */}
                      <Box sx={{
                        p: 1.5,
                        background: 'rgba(102, 126, 234, 0.15)',
                        borderRadius: 1.5,
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(102, 126, 234, 0.25)',
                          borderColor: 'rgba(102, 126, 234, 0.6)',
                        }
                      }}>
                        <TrendingUp sx={{ fontSize: 24, mb: 0.5, opacity: 0.9 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.85, display: 'block', fontSize: '0.8rem' }}>
                          Largest Gift
                        </Typography>
                        {(() => {
                          const value = results.results.consumer_behavior.records[0].contact_info.largest_gift;
                          if (!value || value === 'Not Available') {
                            return <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>N/A</Typography>;
                          }
                          const parts = value.split(' (');
                          const amount = parts[0];
                          const date = parts[1] ? parts[1].replace(')', '') : null;
                          return (
                            <>
                              <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5, fontSize: '1.4rem' }}>
                                {amount}
                              </Typography>
                              {date && (
                                <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.75, display: 'block', fontSize: '0.75rem' }}>
                                  {date}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                      </Box>

                      {/* Latest Gift Tile */}
                      <Box sx={{
                        p: 1.5,
                        background: 'rgba(245, 87, 108, 0.15)',
                        borderRadius: 1.5,
                        border: '1px solid rgba(245, 87, 108, 0.3)',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(245, 87, 108, 0.25)',
                          borderColor: 'rgba(245, 87, 108, 0.6)',
                        }
                      }}>
                        <Schedule sx={{ fontSize: 24, mb: 0.5, opacity: 0.9 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.85, display: 'block', fontSize: '0.8rem' }}>
                          Latest Gift
                        </Typography>
                        {(() => {
                          const value = results.results.consumer_behavior.records[0].contact_info.latest_gift;
                          if (!value || value === 'Not Available') {
                            return <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>N/A</Typography>;
                          }
                          const parts = value.split(' (');
                          const amount = parts[0];
                          const date = parts[1] ? parts[1].replace(')', '') : null;
                          return (
                            <>
                              <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5, fontSize: '1.4rem' }}>
                                {amount}
                              </Typography>
                              {date && (
                                <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.75, display: 'block', fontSize: '0.75rem' }}>
                                  {date}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                      </Box>

                      {/* First Gift Tile */}
                      <Box sx={{
                        p: 1.5,
                        background: 'rgba(79, 172, 254, 0.15)',
                        borderRadius: 1.5,
                        border: '1px solid rgba(79, 172, 254, 0.3)',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(79, 172, 254, 0.25)',
                          borderColor: 'rgba(79, 172, 254, 0.6)',
                        }
                      }}>
                        <DateRange sx={{ fontSize: 24, mb: 0.5, opacity: 0.9 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.85, display: 'block', fontSize: '0.8rem' }}>
                          First Gift
                        </Typography>
                        {(() => {
                          const value = results.results.consumer_behavior.records[0].contact_info.first_gift;
                          if (!value || value === 'Not Available') {
                            return <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>N/A</Typography>;
                          }
                          const parts = value.split(' (');
                          const amount = parts[0];
                          const date = parts[1] ? parts[1].replace(')', '') : null;
                          return (
                            <>
                              <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5, fontSize: '1.4rem' }}>
                                {amount}
                              </Typography>
                              {date && (
                                <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.75, display: 'block', fontSize: '0.75rem' }}>
                                  {date}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                      </Box>

                      {/* Lifetime Giving Tile */}
                      <Box sx={{
                        p: 1.5,
                        background: 'rgba(67, 233, 123, 0.15)',
                        borderRadius: 1.5,
                        border: '1px solid rgba(67, 233, 123, 0.3)',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(67, 233, 123, 0.25)',
                          borderColor: 'rgba(67, 233, 123, 0.6)',
                        }
                      }}>
                        <AttachMoney sx={{ fontSize: 24, mb: 0.5, opacity: 0.9 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.85, display: 'block', fontSize: '0.8rem' }}>
                          Lifetime Giving
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5, fontSize: '1.4rem' }}>
                          {(() => {
                            const value = results.results.consumer_behavior.records[0].contact_info.lifetime_giving;
                            if (!value || value === 'Not Available') return 'N/A';
                            const match = value.match(/^\$[\d,.]+(\.[ 0-9]{2})?/);
                            return match ? match[0] : value;
                          })()}
                        </Typography>
                      </Box>
                    </Box>
                  ) : null}
                </Box>
                
                {/* Source Badge - positioned absolutely at extreme right */}
                {results?.source === 'database' || results?.message?.includes('KnowledgeCore') ? (
                  <Chip
                    label="KC/GT Database"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      backgroundColor: '#4caf50', // Green for database
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 24,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      animation: 'fadeInRight 0.8s ease-out 0.6s both',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src="/Experian-Logo.png"
                    alt="Experian Logo"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      height: 40,
                      width: 'auto',
                      maxWidth: 150,
                      animation: 'fadeInRight 0.8s ease-out 0.6s both',
                      '&:hover': {
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
                        transform: 'scale(1.02)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  />
                )}
              </Box>
            )}

            
            <Paper elevation={3} sx={{ 
              p: 3, 
              minHeight: 'calc(100vh - 140px)', 
              position: 'relative', 
              zIndex: 10,
              marginTop: 0,
              borderRadius: 3
            }}>
              <Typography variant="h6" gutterBottom>
                Search Results
              </Typography>
              {results ? (
                <TabbedResults data={results} searchCriteria={searchCriteria || undefined} />
              ) : (
                <Box sx={{ py: 4, textAlign: 'center', color: '#999' }}>
                  <Typography variant="body1">
                    No search results yet. Enter a name and click Search to get started.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      )}

      <Backdrop
        sx={{ backgroundColor: 'transparent', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <video 
          autoPlay 
          loop 
          muted 
          style={{ 
            width: '40px', 
            height: '40px', 
            marginRight: '12px'
          }}
        >
          <source src="/rolling_com.mp4" type="video/mp4" />
        </video>
        <Typography variant="h6" sx={{ ml: 2, color: '#424242' }}>
          KnowledgeCore Global is Analyzing...
        </Typography>
      </Backdrop>
      
      <BackToTop />

      {/* Profile Settings Modal */}
      <ProfileSettingsModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        currentTheme={currentTheme}
        onThemeChange={(mode) => {
          window.dispatchEvent(new CustomEvent('kc:setTheme', { detail: mode }));
        }}
      />
    </Container>
    </>
  );
};

// Main App component with providers
function App() {
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(
    (localStorage.getItem('kc:pref:theme') as 'light' | 'dark') || 'light'
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const mode = (e as CustomEvent).detail as 'light' | 'dark';
      setThemeMode(mode);
    };
    window.addEventListener('kc:setTheme', handler);
    return () => window.removeEventListener('kc:setTheme', handler);
  }, []);

  return (
    <ThemeProvider theme={buildTheme(themeMode)}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;